import type { FC, ReactNode } from 'react';

import {
  Checkbox,
  Chip,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  Image,
  Box,
  Rating,
} from '@packages/shared';

import type { BasicFilterValue } from '../../api/rest/types';

export type BasicFilterValueItemProps = {
  /** The individual filter value to render */
  value: BasicFilterValue;
  /** If `true`, renders radio buttons instead of checkboxes */
  isSingleSelection?: boolean;
  /** If `true`, this item is displayed as selected */
  isSelected?: boolean;
  /** Callback fired when this filter value item is clicked */
  onClick?: (itemId: string) => void;
};

const Addon = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      marginRight: 1,
      width: 28,
      height: 28,
      borderRadius: '50%',
      border: 1,
      borderColor: 'grey.main',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

const ColorAddon = ({ color }: { color: string }) => (
  <Addon>
    <Box sx={{ backgroundColor: color, width: 1, height: 1 }} />
  </Addon>
);

const ImageAddon = ({ alt, url }: { alt: string; url: string }) => (
  <Addon>
    <Image src={url} alt={alt} width="28" height="28" />
  </Addon>
);

const RatingAddon = ({ defaultValue }: { defaultValue: number }) => (
  <Box sx={{ mr: 1 }}>
    <Rating defaultValue={defaultValue} readOnly />
  </Box>
);

/**
 * BasicFilterItem component description displayed in storybook
 * */
export const BasicFilterValueItem: FC<BasicFilterValueItemProps> = ({
  value,
  isSingleSelection,
  isSelected: isActive,
  onClick,
}) => {
  const IconComponent = isSingleSelection ? Radio : Checkbox;

  const { id, name, count } = value;

  const labelId = `basic-filter-item-${id}`;

  // TODO: discuss if tooltip should be displayed to enable reading overflowing text, or how text overflow should be handled in general
  // options would be ellipsis (like currently), wrapping (default behaviour of MUI), ...?
  return (
    <ListItem disablePadding>
      <ListItemButton role={undefined} onClick={onClick && (() => onClick(id))} disableRipple>
        <IconComponent
          edge="start"
          checked={isActive}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': labelId }}
          sx={{ marginRight: 0 }}
        />
        {'rating' in value ? (
          <RatingAddon defaultValue={value.rating} />
        ) : (
          'image' in value &&
          ('url' in value.image ? (
            <ImageAddon alt={name} url={value.image.url} />
          ) : (
            <ColorAddon color={value.image.rgb} />
          ))
        )}
        <ListItemText
          id={labelId}
          primary={name}
          primaryTypographyProps={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        />
        <Chip
          label={count}
          sx={{
            marginLeft: 1,
            // hide Chip just visually, to keep consistent height (Chip forces 32px height, compared to 28px with default ListItem and even less with `dense` set)
            opacity: count === 0 ? 0 : 1,
            // text cannot be selected anyways, probably because of the ListItem, so at least show consistent cursor
            pointerEvents: 'none',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};
